import { ColorSpecification, DataDrivenPropertyValueSpecification } from 'maplibre-gl';
import { MetarDisplayMode, rvrMinValues } from '../../../settings/settings';
import { StyleFactory, WeatherLayerSpecification } from '../styling';

export const tafLayers: StyleFactory = (baseId, settings) => {
    const layers: WeatherLayerSpecification[] = [];

    let color: DataDrivenPropertyValueSpecification<ColorSpecification>;
    if (settings.airport.displayMode === MetarDisplayMode.ceiling) {
        // prettier-ignore
        color =  [
            'case',
            ['==', ['get', 'ce'], -1], 'rgb(150,150,150)', // null
            ['<', ['get', 'ce'], 2], 'rgb(255,0,255)',
            ['<', ['get', 'ce'], 5], 'rgb(255,128,165)',
            ['<', ['get', 'ce'], 10], 'rgb(255,165,0)',
            ['<', ['get', 'ce'], 20], 'rgb(255,255,103)',
            ['<', ['get', 'ce'], 30], 'rgb(103,255,103)',
            ['<', ['get', 'ce'], 50], 'rgb(0,131,255)',
            ['<', ['get', 'ce'], 100], 'rgb(51,220,255)',
            ['>=', ['get', 'ce'], 100], 'rgb(135,206,255)',
            'rgb(150,150,150)'
        ];
    } else if (settings.airport.displayMode === MetarDisplayMode.visibility) {
        // prettier-ignore
        color = [
            'case',
            ['==', ['get', 'v'], -1],'rgb(150,150,150)', // null
            ['<', ['get', 'v'], 25],'rgb(255,0,255)',
            ['<', ['get', 'v'], 50],'rgb(255,0,0)',
            ['<', ['get', 'v'], 100],'rgb(255,128,165)',
            ['<', ['get', 'v'], 200],'rgb(255,165,0)',
            ['<', ['get', 'v'], 300],'rgb(255,255,103)',
            ['<', ['get', 'v'], 500],'rgb(103,255,103)',
            ['<', ['get', 'v'], 1000],'rgb(0,131,255)',
            ['>=', ['get', 'v'], 1000],'rgb(135,206,255)',
            'rgb(150,150,150)'
        ];
    } else {
        // prettier-ignore
        color = [
            'case',
            ['==', ['get', 'c'], 1], 'rgba(123,252,0,1)',
            ['==', ['get', 'c'], 2], 'rgba(255,255,0,1)',
            ['==', ['get', 'c'], 3], 'rgba(255,165,0, 1)',
            ['==', ['get', 'c'], 4], 'rgba(255,0,0, 1)',
            ['==', ['get', 'c'], 5], 'rgba(255,0,255,1)',
            'rgba(128,128,128,1)'
        ];
    }

    layers.push({
        id: baseId + '-circle-flight-cat',
        pickable: true,
        source: baseId,
        type: 'circle',
        paint: {
            'circle-radius': ['interpolate', ['linear'], ['zoom'], 0, 1, 5, 5],
            'circle-color': color
        }
    });

    layers.push({
        type: 'symbol',
        id: baseId + '-text',
        source: baseId,
        minzoom: 5,
        layout: {
            'text-field': ['get', 'id'],
            'text-offset': [0, 0.5],
            'text-allow-overlap': true,
            'text-anchor': 'top',
            'text-font': ['Open Sans Regular'],
            'text-size': 10
        },
        paint: {
            'text-color': ['global-state', 'text-color']
        }
    });

    return layers;
};

export const faaDelayLayers: StyleFactory = (baseId, settings) => {
    return [
        {
            type: 'circle',
            id: baseId + '-circle',
            source: baseId,
            pickable: true,
            filter: ['>=', ['get', 'l'], settings.airport.faaDelayMinimum],
            paint: {
                // prettier-ignore
                'circle-radius': [
                    "interpolate",
                    ["linear"],
                    ["zoom"],
                    0,
                    1,
                    5,
                    5,
                ],
                // prettier-ignore
                'circle-color': [
                    'case',
                    ['==', ['get', 'l'], 4],
                        [
                            'case',
                            ['==', ['get', 't'], 0], 'rgba(48,48,240,1)',
                            'rgba(102,153,255,1)'
                        ],
                    ['==', ['get', 'l'], 3], 'rgba(250,125,0,1)',
                    ['==', ['get', 'l'], 2], 'rgba(255,0,0,1)',
                    ['==', ['get', 'l'], 1], 'rgba(255,255,0,1)',
                    ['==', ['get', 'l'], 0], 'rgba(0,220,0,1)',
                    "#808080"
                ]
            }
        },
        {
            type: 'symbol',
            id: baseId + '-text',
            filter: ['>=', ['get', 'l'], settings.airport.faaDelayMinimum],
            source: baseId,
            minzoom: 5,
            layout: {
                'text-field': ['get', 'id'],
                'text-offset': [0, 0.5],
                'text-allow-overlap': true,
                'text-anchor': 'top',
                'text-font': ['Open Sans Regular'],
                'text-size': 10
            },
            paint: {
                'text-color': ['global-state', 'text-color']
            }
        }
    ];
};

export const rvrLayers: StyleFactory = (baseId, settings) => {
    return [
        {
            type: 'symbol',
            id: baseId + '-icon',
            pickable: true,
            source: baseId,
            filter: ['<', ['get', 'mv'], rvrMinValues[settings.airport.rvrMinimum]],
            layout: {
                'icon-image': ['image', 'fe-weather:rect-filled'],
                'icon-size': 0.5,
                'icon-allow-overlap': true
            },
            paint: {
                // prettier-ignore
                'icon-color': [
                    'case',
                    [">=", ['get', 'mv'], 60], 'rgb(0,220,0)', 
                    [">=", ['get', 'mv'], 24], 'rgb(0,131,255)', 
                    [">=", ['get', 'mv'], 12], 'rgb(255,255,0)', 
                    [">=", ['get', 'mv'], 6], 'rgb(250,125,0)', 
                    [">=", ['get', 'mv'], 3], 'rgb(255,0,0)', 
                    'rgb(255,0,255)',
                ]
            }
        },
        {
            type: 'symbol',
            id: baseId + '-text',
            source: baseId,
            minzoom: 5,
            layout: {
                'text-field': ['get', 'id'],
                'text-offset': [0, 0.5],
                'text-allow-overlap': true,
                'text-anchor': 'top',
                'text-font': ['Open Sans Regular'],
                'text-size': 10
            },
            paint: {
                'text-color': ['global-state', 'text-color']
            }
        }
    ];
};

export const diversionLayers: StyleFactory = (baseId) => {
    return [
        {
            type: 'line',
            id: baseId + '-line',
            source: baseId,
            themed: true,
            layout: {
                'line-join': 'round',
                'line-cap': 'round'
            },
            paint: {
                // prettier-ignore
                'line-color': [
                    'case',
                    ['>=', ['get', 'c'], 10], 'rgb(255,0,0)',
                    ['>=', ['get', 'c'], 6], 'rgb(250,125,0)',
                    ['>=', ['get', 'c'], 4], 'rgb(255,255,0)',
                    ['global-state', 'text-color']
                ],
                'line-width': 2
            }
        },
        {
            type: 'symbol',
            id: baseId + '-arrow',
            source: baseId,
            filter: ['==', ['get', 't'], 'a'],
            themed: true,
            minzoom: 5,
            layout: {
                'icon-image': ['image', 'fe-weather:arrow'],
                'icon-offset': [0, 16],
                'icon-size': 0.7,
                'icon-rotate': ['get', 'b'],
                'icon-allow-overlap': true,
                'icon-rotation-alignment': 'map'
            },
            paint: {
                // prettier-ignore
                'icon-color': [
                    'case',
                    ['>=', ['get', 'c'], 10], 'rgb(255,0,0)',
                    ['>=', ['get', 'c'], 6], 'rgb(250,125,0)',
                    ['>=', ['get', 'c'], 4], 'rgb(255,255,0)',
                    ['global-state', 'text-color']
                ]
            }
        },
        {
            type: 'circle',
            id: baseId + '-circ-from',
            pickable: true,
            themed: true,
            source: baseId,
            filter: ['==', ['get', 't'], 'na'],
            paint: {
                // prettier-ignore
                'circle-color': [
                    'case',
                    ['>=', ['get', 'from'], 10], 'rgb(255,0,0)',
                    ['>=', ['get', 'from'], 6], 'rgb(250,125,0)',
                    ['>=', ['get', 'from'], 4], 'rgb(255,255,0)',
                    ['>=', ['get', 'from'], 2], 'rgb(0,0,220)',
                    'rgb(0,220,0)'
                ],
                'circle-stroke-color': 'rgb(128,128,128)',
                'circle-stroke-width': 1,
                'circle-radius': 8
            }
        },
        {
            type: 'circle',
            id: baseId + '-circ-to',
            source: baseId,
            themed: true,
            filter: ['==', ['get', 't'], 'na'],
            paint: {
                // prettier-ignore
                'circle-color': [
                    'case',
                    ['>=', ['get', 'to'], 10], 'rgb(255,0,0)',
                    ['>=', ['get', 'to'], 6], 'rgb(250,125,0)',
                    ['>=', ['get', 'to'], 4], 'rgb(255,255,0)',
                    ['>=', ['get', 'to'], 2], 'rgb(0,0,220)',
                    'rgb(0,220,0)'
                ],
                'circle-stroke-color': 'rgb(128,128,128)',
                'circle-stroke-width': 1,
                'circle-radius': 4
            }
        },
        {
            type: 'symbol',
            id: baseId + '-text',
            source: baseId,
            filter: ['!=', ['get', 't'], 'a'],
            minzoom: 5,
            layout: {
                'text-field': ['get', 'id'],
                'text-offset': [1.25, -1.25],
                'text-allow-overlap': true,
                'text-anchor': 'center',
                'text-font': ['Open Sans Regular'],
                'text-size': 10
            },
            paint: {
                'text-color': ['global-state', 'text-color']
            }
        }
    ];
};
